/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { coreTranslations, CORE_NAMESPACE } from '@opengeoweb/core';
import {
  DATA_EXPLORER_NAMESPACE,
  dataExplorerTranslations,
} from '@opengeoweb/data-explorer';
import {
  FORM_FIELDS_NAMESPACE,
  formFieldsTranslations,
} from '@opengeoweb/form-fields';
import {
  safelyInitI18nForTestsAndStories,
  SHARED_NAMESPACE,
  sharedTranslations,
} from '@opengeoweb/shared';
import {
  TIME_SERIES_NAMESPACE,
  timeseriesTranslations,
} from '@opengeoweb/time-series';
import { WARN_NAMESPACE, warningsTranslations } from '@opengeoweb/warnings';
import {
  WEBMAP_REACT_NAMESPACE,
  webmapReactTranslations,
} from '@opengeoweb/webmap-react';
import workspaceTranslations from '../../../locales/workspace.json';

export const WORKSPACE_NAMESPACE = 'workspace';

export const initWorkspaceTestI18n = (): void => {
  safelyInitI18nForTestsAndStories(i18n, WORKSPACE_NAMESPACE, [
    { ns: WORKSPACE_NAMESPACE, translations: workspaceTranslations },
    { ns: SHARED_NAMESPACE, translations: sharedTranslations },
    { ns: CORE_NAMESPACE, translations: coreTranslations },
    { ns: WEBMAP_REACT_NAMESPACE, translations: webmapReactTranslations },
    { ns: FORM_FIELDS_NAMESPACE, translations: formFieldsTranslations },
    { ns: WARN_NAMESPACE, translations: warningsTranslations },
    { ns: TIME_SERIES_NAMESPACE, translations: timeseriesTranslations },
    { ns: DATA_EXPLORER_NAMESPACE, translations: dataExplorerTranslations },
  ]);
};

const ns = [WORKSPACE_NAMESPACE, SHARED_NAMESPACE];

export const translateInTestsAndStories = (
  key: string,
  params: Record<string, string | number> | undefined = undefined,
): string => i18n.t(key, { ns, ...params });

export const useWorkspaceTranslation = (): UseTranslationResponse<
  typeof WORKSPACE_NAMESPACE,
  typeof i18n
> => useTranslation(ns);

export { i18n };
